import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import customerService from "../../httpService/CustomerService";
import { CustomerType } from "../../model/customer/customer";
import { FileFromObjectUrl } from "../../model/common/file-from-object-url";

export type CustomerRegistrationState = {
    loading: boolean
    currentStep: number
    finishedSteps: number[]
    personalInformation: CustomerRegistrationPersonalInformation
    employmentInformation: CustomerRegistrationEmploymentInformation
    identityInformation: CustomerRegistrationIdentityInformation
    financialInformation: CustomerRegistrationFinancialInformation
}

export type CustomerRegistrationPersonalInformation = {
    firstName?: string
    lastName?: string
    email?: string
    emailVerified?: boolean
    phoneNumber?: string
    phoneNumberVerified?: boolean
    state?: string
    city?: string
    address?: string
    meterNumber?: string
    electricityDistributionCompany?: string
    addressProofFile?: FileFromObjectUrl 
    isHouseAddressSameAsInstallationAddress?: boolean
    installationAddress?: string
}

export type CustomerRegistrationEmploymentInformation = {
    companyName?: string
    jobTitle?: string
    dateOfEmployment?: string
    employmentType?: string
    employmentContractFile?: FileFromObjectUrl
    linkedInProfileUrl?: string
}

export type CustomerRegistrationIdentityInformation = {
    photoFile?: FileFromObjectUrl
    identificationType: string
    identificationNumber: string
}

export type CustomerRegistrationFinancialInformation = {
    // Information for redux
    scope?: string
    bvn?: string
    bvnVerified?: boolean
    methods?: InitiateLookupDataMethods[]
    selectedMethodNumber?: number
    selectedMethodName?: string
    alternatePhoneNumber?: string
    sessionId?: string
    monoVerified?: boolean


    // Other
    // bvnInitialization?: BvnInitialization
    // bvnVerification?: BvnVerification
    // bvnDetails?: BvnDetails
    // monoDetails?: MonoDetails
    // finishCustomerRegistration?: FinishCustomerRegistration
}

export type BvnInitializationRequest = {
    bvn?: string
    scope?: string
}

export type BvnInitializationResponse = {
    sessionId?: string
    methods?: InitiateLookupDataMethods[]
}

export type InitiateLookupDataMethods = {
    method?: string
    hint?: string
}

export type BvnVerificationRequest = {
    sessionId?: string
    method?: string
    methodNumber?: number
    phoneNumber?: string
}

export type BvnVerificationResponse = {
    message?: string
    status?: string
}

export type BvnDetailsRequest = {
    otp?: string
    sessionId?: string
}

export type BvnDetailsResponse = {
    identityData?: BvnIdentityData
}

export type BvnIdentityData = {
    status?: string
    message?: string
    data: any
}

export type MonoDetailsRequest = {
    monoTemporaryToken?: string
}

export type FinishCustomerRegistrationRequest = {
    password?: string
    confirmPassword?: string,
    productDetailsId?: string,
    durationOfPaymentsInMonths?: number,
    installationAddress: string,
    roofType?: number
}

const initiateVerifyPhoneNumber = createAsyncThunk<void, {
    phoneNumber: string,
}, { state: RootState }>(
    'customerRegistration/initiateVerifyPhoneNumber',
    async (request, { getState, rejectWithValue }) => {
        try {
            await customerService.initiateVerifyPhoneNumber({
                phoneNumber: request.phoneNumber,
            });
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const verifyPhoneNumber = createAsyncThunk<void, {
    otp: string,
}, { state: RootState }>(
    'customerRegistration/verifyPhoneNumber',
    async (request, { getState, rejectWithValue }) => {
        try {
            await customerService.validatePhoneNumber({
                token: request.otp,
            });
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const finishPersonalInformation = createAsyncThunk<CustomerRegistrationPersonalInformation, CustomerRegistrationPersonalInformation, { state: RootState }>(
    'customerRegistration/finishPersonalInformation',
    async (request, { getState, rejectWithValue }) => {
        try {
            await customerService.updatePersonalInformation({
                personalInformation: request,
            });

            return request;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const finishEmploymentInformation = createAsyncThunk<{
    companyName: string,
    jobTitle: string,
    joinDate: Date,
    employmentContract: FileFromObjectUrl,
    employmentType: string,
    linkedInPage?: string
}, {
    companyName: string,
    jobTitle: string,
    joinDate: Date,
    employmentContract: FileFromObjectUrl,
    employmentType: string,
    linkedInPage?: string
}, { state: RootState }>(
    'customerRegistration/finishEmploymentInformation',
    async (request, { getState, rejectWithValue }) => {
        try {
            await customerService.updateEmploymentInformation({
                employmentInformation: request,
            });

            return request;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const bvnInitializeThunk = createAsyncThunk<BvnInitializationResponse, BvnInitializationRequest, { state: RootState }>(
    'customerRegistration/bvnInitialization',
    async (request, { getState, rejectWithValue }) => {
        try {
            const response = await customerService.initializeBvn({
                bvn: request.bvn!,
                scope: request.scope!
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const bvnVerifyThunk = createAsyncThunk<BvnVerificationResponse, BvnVerificationRequest, { state: RootState }>(
    'customerRegistration/bvnVerification',
    async (request, { getState, rejectWithValue }) => {
        try {
            const response = await customerService.verifyBvn({
                sessionId: request.sessionId!,
                method: request.method!,
                phoneNumber: request.method === 'alternate_phone' ? request.phoneNumber! : ''
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const bvnDetailsThunk = createAsyncThunk<BvnDetailsResponse, BvnDetailsRequest, { state: RootState }>(
    'customerRegistration/bvnDetails',
    async (request, { getState, rejectWithValue }) => {
        try {
            const response = await customerService.detailsBvn({
                otp: request.otp!,
                sessionId: request.sessionId!
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

const saveMonoThunk = createAsyncThunk<any, MonoDetailsRequest, { state: RootState }>(
    'customerRegistration/saveMono',
    async (request, { getState, rejectWithValue }) => {
        try {
            const response = await customerService.updateMono({
                monoTemporaryToken: request.monoTemporaryToken!,
            });

            return response;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    },
)

function extractPersonalInformationFromCustomer(customer: CustomerType): CustomerRegistrationPersonalInformation {
    return {
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        emailVerified: true,
        address: customer.address ?? '',
        addressProofFile : customer.addressProofUrl ? {
            name: customer.addressProofUrl?.split('/').pop()?.split('?')[0],
        } as FileFromObjectUrl : undefined,
        city: customer.city ?? '',
        state: customer.state ?? '',
        phoneNumber: customer.phoneNumber ?? '',
        phoneNumberVerified: !!customer.phoneNumber,
        meterNumber: customer.meterNumber,
        electricityDistributionCompany: customer.electricityDistributionCompany,
        isHouseAddressSameAsInstallationAddress: true
    }
}

function extractEmploymentInformationFromCustomer(customer: CustomerType): CustomerRegistrationEmploymentInformation {
    return {
        companyName: customer.companyName,
        jobTitle: customer.jobTitle,
        dateOfEmployment: customer.joinedAt,
        employmentType: customer.employmentType,
        employmentContractFile: customer.employmentContractUrl ? {
            name: customer.employmentContractUrl?.split('/').pop()?.split('?')[0],
        } as FileFromObjectUrl : undefined,
        linkedInProfileUrl: customer.linkedInUrl
    }
}

function extractIdentityInformationFromCustomer(customer: CustomerType): CustomerRegistrationIdentityInformation {
    return {
        photoFile: customer.photoUrl ? {
            name: customer.photoUrl?.split('/').pop()?.split('?')[0] ,
        } as FileFromObjectUrl : undefined,
        identificationNumber: customer.idCardNumber ?? '',
        identificationType: customer.idCardType ?? ''
    }
}

function extractFinancialInformationFromCustomer(customer: CustomerType): CustomerRegistrationFinancialInformation {
    return {
        bvn: customer.bvn,
        bvnVerified: !!customer.bvn,
        methods: [],
        sessionId: '',
        monoVerified: customer.monoVerified
    }
}

function getFinishingStep(customer: CustomerType): number[] {
    const result = [];

    if(customer.address)
        result.push(1);

    if(customer.companyName)
        result.push(2);

    if(customer.idCardType)
        result.push(3);

    if(customer.monoVerified)
        result.push(4);

    return result;
}

function extractStateFromSessionStorage(): CustomerRegistrationState {
    const stateFromStorage = sessionStorage.getItem('customer-registration-form')

    return stateFromStorage ? JSON.parse(stateFromStorage) : {
        loading: false,
        currentStep: 1,
        finishedSteps: [],
        employmentInformation: {},
        financialInformation: {},
        identityInformation: {},
        personalInformation: {}
    }
}

const updateIdentityInformation = createAsyncThunk<
    CustomerRegistrationIdentityInformation,
    CustomerRegistrationIdentityInformation,
    { state: RootState }
>(
    'customerRegistration/updateIdentityInformation',
    async (request, { getState, rejectWithValue }) => {
        try {
            console.log(request.identificationType)
            await customerService.updateIdentityInformation({
                identityPhoto: request.photoFile,
                identityType: request.identificationType,
                identityNumber: request.identificationNumber,
            });

            return request;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Something went wrong. Please try again later.");
        }
    }
);


function saveStateToSessionStorage(state: CustomerRegistrationState) {
    sessionStorage.setItem('customer-registration-form', JSON.stringify(state))
}

export const customerRegistrationSlice = createSlice({
    name: 'customerRegistration',
    initialState: extractStateFromSessionStorage(),
    selectors: {
        currentStepSelector: (state: CustomerRegistrationState) => state.currentStep,
        finishedStepsSelector: (state: CustomerRegistrationState) => state.finishedSteps,
        personalInformationSelector: (state: CustomerRegistrationState) => state.personalInformation,
        emailVerifiedSelector: (state: CustomerRegistrationState) => state.personalInformation.emailVerified,
        phoneNumberVerifiedSelector: (state: CustomerRegistrationState) => state.personalInformation.phoneNumberVerified,
        employmentInformationSelector: (state: CustomerRegistrationState) => state.employmentInformation,
        identityInformationSelector: (state: CustomerRegistrationState) => state.identityInformation,
        FinancialInformationSelector: (state: CustomerRegistrationState) => state.financialInformation,
    },
    reducers: {
        initialize: (state, action: PayloadAction<CustomerType>) => {
            state.personalInformation = extractPersonalInformationFromCustomer(action.payload);
            state.employmentInformation = extractEmploymentInformationFromCustomer(action.payload);
            state.identityInformation = extractIdentityInformationFromCustomer(action.payload);
            state.financialInformation = extractFinancialInformationFromCustomer(action.payload);
            state.finishedSteps = getFinishingStep(action.payload);
            state.currentStep = state.finishedSteps.length > 0 ? Math.min(state.finishedSteps[state.finishedSteps.length - 1] + 1, 4) : 1;
            saveStateToSessionStorage(state);
        },
        moveToStep: (state, action: PayloadAction<number>) => {
            state.currentStep = action.payload;
        },
        moveToNextStep: (state) => {
            if (state.currentStep < 4) {
                state.currentStep += 1;
            }
        },
        moveToPreviousStep: (state) => {
            if (state.currentStep > 1) {
                state.currentStep -= 1;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initiateVerifyPhoneNumber.fulfilled, (state, payload) => {
            state.personalInformation.phoneNumber = payload.meta.arg.phoneNumber;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(initiateVerifyPhoneNumber.rejected, (state) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(initiateVerifyPhoneNumber.pending, (state) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });
        builder.addCase(verifyPhoneNumber.fulfilled, (state) => {
            state.personalInformation.phoneNumberVerified = true;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(verifyPhoneNumber.rejected, (state) => {
            state.personalInformation.phoneNumberVerified = false;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(verifyPhoneNumber.pending, (state) => {
            state.personalInformation.phoneNumberVerified = false;
            state.loading = true;
            saveStateToSessionStorage(state);
        });
        builder.addCase(finishPersonalInformation.fulfilled, (state, action) => {
            state.personalInformation.firstName = action.payload.firstName;
            state.personalInformation.lastName = action.payload.lastName;
            state.personalInformation.email = action.payload.email;
            state.personalInformation.phoneNumber = action.payload.phoneNumber;
            state.personalInformation.state = action.payload.state;
            state.personalInformation.city = action.payload.city;
            state.personalInformation.isHouseAddressSameAsInstallationAddress = action.payload.isHouseAddressSameAsInstallationAddress;
            state.personalInformation.address = action.payload.address;
            state.personalInformation.meterNumber = action.payload.meterNumber;
            state.personalInformation.electricityDistributionCompany = action.payload.electricityDistributionCompany;
            state.personalInformation.addressProofFile = {
                name: action.payload.addressProofFile?.name
            } as FileFromObjectUrl;
            state.personalInformation.installationAddress = action.payload.installationAddress;

            if (state.finishedSteps.indexOf(1) === -1) {
                state.finishedSteps.push(1);
            }
            state.currentStep = 2;
            state.loading = false;

            saveStateToSessionStorage(state);
        });
        builder.addCase(finishPersonalInformation.rejected, (state) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(finishPersonalInformation.pending, (state) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });
        builder.addCase(finishEmploymentInformation.fulfilled, (state, action) => {
            state.employmentInformation.companyName = action.payload.companyName;
            state.employmentInformation.jobTitle = action.payload.jobTitle;
            state.employmentInformation.dateOfEmployment = action.payload.joinDate.toISOString();
            state.employmentInformation.employmentType = action.payload.employmentType;
            state.employmentInformation.employmentContractFile = {
                name: action.payload.employmentContract.name
            } as FileFromObjectUrl;
            state.employmentInformation.linkedInProfileUrl = action.payload.linkedInPage;

            if (state.finishedSteps.indexOf(2) === -1) {
                state.finishedSteps.push(2);
            }
            state.currentStep = 3;
            state.loading = false;

            saveStateToSessionStorage(state);
        });
        builder.addCase(finishEmploymentInformation.rejected, (state) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(finishEmploymentInformation.pending, (state) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });
        builder.addCase(updateIdentityInformation.fulfilled, (state, action) => {
            state.identityInformation.photoFile = {
                name: action.meta.arg.photoFile?.name,
            } as FileFromObjectUrl;
            state.identityInformation.identificationType = action.payload.identificationType;
            state.identityInformation.identificationNumber = action.payload.identificationNumber;

            if (state.finishedSteps.indexOf(3) === -1) {
                state.finishedSteps.push(3);
            }
            state.currentStep = 4;
            state.loading = false;

            saveStateToSessionStorage(state);
        });
        builder.addCase(updateIdentityInformation.rejected, (state) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(updateIdentityInformation.pending, (state) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });

        builder.addCase(bvnInitializeThunk.fulfilled, (state, action) => {
            state.financialInformation.bvn = action.meta.arg.bvn;
            state.financialInformation.methods= action.payload.methods;
            state.financialInformation.sessionId= action.payload.sessionId;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnInitializeThunk.rejected, (state, action) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnInitializeThunk.pending, (state, action) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });

        builder.addCase(bvnVerifyThunk.fulfilled, (state, action) => {
            state.financialInformation.alternatePhoneNumber = action.meta.arg.phoneNumber;
            state.financialInformation.selectedMethodNumber = action.meta.arg.methodNumber;
            state.financialInformation.selectedMethodName = action.meta.arg.method;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnVerifyThunk.rejected, (state, action) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnVerifyThunk.pending, (state, action) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });

        builder.addCase(bvnDetailsThunk.fulfilled, (state, action) => {
            state.financialInformation.bvnVerified = true;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnDetailsThunk.rejected, (state, action) => {
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(bvnDetailsThunk.pending, (state, action) => {
            state.loading = true;
            saveStateToSessionStorage(state);
        });

        builder.addCase(saveMonoThunk.fulfilled, (state, action) => {
            state.financialInformation.monoVerified = true;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(saveMonoThunk.rejected, (state, action) => {
            state.financialInformation.monoVerified = false;
            state.loading = false;
            saveStateToSessionStorage(state);
        });
        builder.addCase(saveMonoThunk.pending, (state, action) => {
            state.financialInformation.monoVerified = false;
            state.loading = true;
            saveStateToSessionStorage(state);
        });
    }
});

export const {
    initialize,
    moveToNextStep,
    moveToPreviousStep,
    moveToStep,
} = customerRegistrationSlice.actions;

export const {
    currentStepSelector,
    finishedStepsSelector,
    personalInformationSelector,
    emailVerifiedSelector,
    phoneNumberVerifiedSelector,
    employmentInformationSelector,
    identityInformationSelector,
    FinancialInformationSelector
} = customerRegistrationSlice.selectors;

export {
    initiateVerifyPhoneNumber,
    verifyPhoneNumber,
    finishPersonalInformation,
    finishEmploymentInformation,
    updateIdentityInformation,
    bvnInitializeThunk,
    bvnVerifyThunk,
    bvnDetailsThunk,
    saveMonoThunk,
}

export default customerRegistrationSlice.reducer;